import moment from "moment";
import FileSaver from "file-saver";
import { toastr } from "react-redux-toastr";
import { DEFAULT_DATE_FORMAT, DISPLAY_DATE_FORMAT, isNullEmptyOrWhitespace, MAX_FILE_SIZE, FullBaseUrl } from "../constants";

let formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  
    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
  });

/**
 * @template T
 * @param {T | T[]} value 
 * @returns {T[]}
 */
export const mustBeArray = (value) => {
    return Array.isArray(value) ? value : [];
};

export const getUserManagerConfigs = () => {
    let config = localStorage.getItem('configs');
    try{
        config = JSON.parse(config);
    }catch(error){
        console.log(error);
    }
    const instance =  config && config.instance;
    const tenant = config && config.tenant;
    const scope = config && config.scope;
    const authority = config && config.authority;
    const client_id = config && config.clientId;
 
    const configs = {
        instance, 
        tenant,
        cacheLocation: "sessionStorage",
        signInPolicy: 'B2C_1A_SIGNUP_SIGNIN',
        scope,
        authority,
        client_id,
        redirect_uri: `${FullBaseUrl()}/callback`,
        post_logout_redirect_uri: `${FullBaseUrl()}/callback`,
        response_type: 'token id_token ',
        session_state: window.location.pathname,
        automaticSilentRenew: false,
        filterProtocolClaims: true,
        loadUserInfo: false,
        extraQueryParams: {
            resource: "2fc8d44c-1db9-4fa2-8b0f-841e0f89c056"
        }
    };
    return(configs);
};

export const trimWhiteSpaces = (value) => {
    if(!value) return "";
    return value.trim();
};

export const getErrorMessage = ({x}) => {
    if(!x) return "";
    // x.errors holds the parsed response body (see performFetch). Prefer a server-supplied
    // message (camelCase x.errorMessage, or PascalCase/camelCase on the body) over the bare
    // HTTP statusText in x.error, so messages like the 409 "export already in progress" surface.
    const body = x.errors;
    if (x.errorMessage) return x.errorMessage;
    if (body && (body.Message || body.message)) return body.Message || body.message;
    if (x.error) return x.error;
    return (x.data && (x.data.Message || x.data.message)) || "";
};

export const requestHasError = ({x}) => {
    if(x && (x.error || x.data.Message || x.errorMessage)) return true;
    return false;
};

export const currencyFormater = value => {
    if(!value || _.isNaN(value)) return `$0.00`;
    return formatter.format(Number(value));
};

export const volumeFormater = value => {
    return !value ? '0L' : `${Number(Math.round(value + 'e' + 1) + 'e-' + 1)}L`;
};

export const displayDateFormater = ({value, format=""}) => {
    if(!value) return "";
    if(!format) return moment(value).format(DISPLAY_DATE_FORMAT);
    return moment(value, format).format(DISPLAY_DATE_FORMAT);
};

export const displayDefaultDateFormater = ({value, format="", customReturnFormat=""}) => {
    if(!value) return "";
    if(!format && !customReturnFormat) return moment(value).format(DEFAULT_DATE_FORMAT);
    return moment(value, format).format(customReturnFormat ? customReturnFormat : DEFAULT_DATE_FORMAT);
};

export const getPrimaryOrSecondaryAddress = ({clientAddresses}) => {
    let primaryAddresss = _.find(mustBeArray(clientAddresses), o => o.isPrimary);
    if(!primaryAddresss){
        primaryAddresss = mustBeArray(clientAddresses)[0];
    }
    return primaryAddresss;
};

export const displayAddressFormater = ({address}) => {
    if(!address) return "";
    let streetAddress = isNullEmptyOrWhitespace(address.line2)
                    ? address.line1
                    : `${address.line1} ${address.line2}`; 
    return `${streetAddress}${`${address.suburbName? `, ${address.suburbName}`: ""}`} ${address.state || ""} ${address.postCode || ""}`;
};

export const isUploadedFileValid = (e) => {
    if(!e || (e.base64 === "")){
        toastr.error("Bad file");
        return false;
    }
    let uploadedFilesHaveValidSize = true;
    mustBeArray(e.filelist).forEach(file => {
        if(file.size > MAX_FILE_SIZE){
            uploadedFilesHaveValidSize = false;
        }
    });
    if(!uploadedFilesHaveValidSize){
        toastr.error('Files must be less than 5 Mb');
        return false;
    }
    return true;
};

export const addFiltersToUrl = ({filters={}, url}) => {
    if(!filters || _.isEmpty(filters)) return url;
    let combinedFilters = Object.keys(filters).reduce((a,key) => {
        const value = filters[key];
        if(value === null || value === undefined || value === "") return a;
        if(Array.isArray(value)){
            if(value.length === 0) return a;
            const pairs = value.map(v => `${key}=${encodeURIComponent(v)}`).join("&");
            return a ? `${a}&${pairs}` : pairs;
        }
        const pair = `${key}=${encodeURIComponent(value)}`;
        return a ? `${a}&${pair}` : pair;
    },"");
    return combinedFilters ? `${url}?${combinedFilters}` : url;
};

// Conver uploaded files to base 64
export const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file)
      fileReader.onload = () => {
        return(resolve(fileReader.result));
      };
      fileReader.onerror = (error) => {
        return(reject(error));
      };
    });
  };

 export const getActionTypeFromUrl = () => {
    const searchField = window.location.search;
    if(isNullEmptyOrWhitespace(searchField)) return "";
    return searchField.split("=")[1];
 };

 export const getMinRowsInTable = (list, defaultValue=20) => {
    if (mustBeArray(list).length === 0) return 5;
    return mustBeArray(list).length > defaultValue ? defaultValue : mustBeArray(list).length;
 }

 const escapeCsvField = (value) => {
    if(value === null || value === undefined) return "";
    const stringValue = String(value);
    return /[",\r\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
 };

 // Builds a CSV from an already-loaded array (no API call) and triggers a browser download.
 // columns: [{header, accessor: row => value}]
 export const exportArrayAsCsv = ({rows, columns, filename}) => {
    const headerRow = columns.map(c => escapeCsvField(c.header)).join(",");
    const dataRows = mustBeArray(rows).map(row => columns.map(c => escapeCsvField(c.accessor(row))).join(","));
    const csvContent = [headerRow, ...dataRows].join("\r\n");
    FileSaver.saveAs(new Blob([csvContent], {type: "text/csv;charset=utf-8;"}), filename);
 };